'use client';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { forwardRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

// Re-exports for advanced cases (Provider lives in providers.tsx; most
// callers just use <Tooltip label="…">{trigger}</Tooltip> below).
export const TooltipProvider = TooltipPrimitive.Provider;
export const TooltipRoot = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

export const TooltipContent = forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 rounded-lg border border-hairline bg-surface px-2 py-1 font-mono text-[10px] uppercase tracking-eyebrow text-foreground shadow-lg',
        'data-[state=delayed-open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out data-[state=delayed-open]:fade-in',
        'data-[state=closed]:zoom-out-95 data-[state=delayed-open]:zoom-in-95',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = 'TooltipContent';

interface TooltipProps {
  label: string;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Delay in ms before the tooltip opens. Defaults to 300. */
  delayDuration?: number;
}

// Convenience wrapper — covers the 90% case of "icon button needs a label".
// Pass any element as children; Radix `asChild` patches the event handlers
// onto it without adding an extra DOM node.
export function Tooltip({
  label,
  children,
  side = 'bottom',
  align = 'center',
  delayDuration = 300,
}: TooltipProps) {
  return (
    <TooltipRoot delayDuration={delayDuration}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} align={align}>
        {label}
      </TooltipContent>
    </TooltipRoot>
  );
}
